// =============================================================================
// equipmentShared — the one place for the equipment register's vocabulary
// =============================================================================
// 22 Oct 2026 (mig 444). James: "visually I want it re-done for the field guys
// on phone … not just boring tables"; managers "track, assign and monitor …
// with visual widgets"; auditors "filter easily and export". Everything the
// equipment screens share lives here so the words and colours are the same
// on the phone, in HR and on the export:
//   · RegisterRow — a row of v_equipment_register (rigging + power tools +
//     ladders, one vocabulary: status in_service | quarantined; registration
//     pending | approved; urgency out_of_service | overdue | due_soon |
//     never_inspected | current).
//   · urgency labels / tones, the tag-colour chip, date helpers.
//   · hooks: useEquipmentRegister (the view), useEquipmentKits, useOpenRequests.
//   · the one-tap actions as mutations: request, allocate, approve, decline,
//     resolve defect — thin wrappers over the app.* RPCs 444 added.
//   · toExportRow — maps a RegisterRow onto the road-week export's shape so
//     the Excel / PDF export is the same file whichever screen asks for it.
//   · ExportDialog — the road-week dialog, lifted out of
//     InspectionRegisterScreen so the phone and HR screens can use it.
// =============================================================================

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, FileSpreadsheet, FileText, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button, Field, Input, type Tone } from "@/components/ui";
import type { RegisterExportRow } from "@/features/inspections/inspectionRegisterExport";

export type Kind = "rigging" | "power_tool" | "ladder";
export type Urgency = "out_of_service" | "overdue" | "due_soon" | "never_inspected" | "current";

export interface RegisterRow {
  kind:                     Kind;
  item_id:                  string;
  code:                     string;
  asset_tag:                string | null;
  display_name:             string | null;
  item_class:               string | null;
  item_type:                string | null;
  make:                     string | null;
  model:                    string | null;
  serial_number:            string | null;
  swl_kg:                   number | null;
  photo_url:                string | null;
  kit_id:                   string | null;
  kit_name:                 string | null;
  kit_vehicle_id:           string | null;
  kit_vehicle_code:         string | null;
  kept_in_vehicle_code:     string | null;
  holder_employee_id:       string | null;
  holder_name:              string | null;
  registration_status:      "pending" | "approved";
  submitted_by_employee_id: string | null;
  submitted_by_name:        string | null;
  status:                   "in_service" | "quarantined";
  tag_colour:               string | null;
  tag_number:               string | null;
  last_inspection_date:     string | null;
  last_result:              string | null;
  next_due:                 string | null;
  last_inspector_employee_id: string | null;
  last_inspector_name:      string | null;
  open_defects:             number;
  urgency:                  Urgency;
}

export interface Kit {
  kit_id:       string;
  kit_name:     string;
  vehicle_id:   string | null;
  is_active:    boolean;
  notes:        string | null;
  vehicle?:     { vehicle_code: string; make: string | null; model: string | null; rego: string | null; assigned_to_employee_id: string | null } | null;
}

export interface EquipmentRequest {
  request_id:                string;
  kind:                      Kind | "hand_tool";
  item_id:                   string;
  requested_by_employee_id:  string;
  requested_for_employee_id: string | null;
  note:                      string | null;
  status:                    "open" | "allocated" | "declined";
  created_at:                string;
  requested_by?:             { first_name: string | null; last_name: string | null } | null;
}

// ---- words and colours ------------------------------------------------------

export const KIND_LABEL: Record<Kind, string> = { rigging: "Rigging", power_tool: "Power tool", ladder: "Ladder" };
export const KIND_PLURAL: Record<Kind, string> = { rigging: "Rigging", power_tool: "Power tools", ladder: "Ladders" };

export const URGENCY_LABEL: Record<Urgency, string> = {
  out_of_service:  "Out of service",
  overdue:         "Overdue",
  due_soon:        "Due soon",
  never_inspected: "Never inspected",
  current:         "Current",
};
export const URGENCY_TONE: Record<Urgency, Tone> = {
  out_of_service:  "critical",
  overdue:         "overdue",
  due_soon:        "warning",
  never_inspected: "notice",
  current:         "ok",
};
/** Tailwind text colour for an urgency word. */
export const URGENCY_TEXT: Record<Urgency, string> = {
  out_of_service:  "text-danger",
  overdue:         "text-danger",
  due_soon:        "text-warn",
  never_inspected: "text-info",
  current:         "text-ok",
};
/** Card border/background for an urgency word — the phone cards. */
export const URGENCY_CARD: Record<Urgency, string> = {
  out_of_service:  "border-danger/60 bg-danger/10",
  overdue:         "border-danger/50 bg-danger/5",
  due_soon:        "border-warn/50",
  never_inspected: "border-info/40",
  current:         "",
};
export const URGENCY_ORDER: Urgency[] = ["out_of_service", "overdue", "due_soon", "never_inspected", "current"];

/** The physical tag colours (quarterly: Red Jan–Mar · Green Apr–Jun · Blue Jul–Sep · Yellow Oct–Dec). */
export const TAG_SWATCH: Record<string, string> = {
  Red:    "#e5484d",
  Green:  "#5ebf73",
  Blue:   "#6ca3e5",
  Yellow: "#e8b547",
};

export function daysUntil(key: string | null | undefined): number | null {
  if (!key) return null;
  const [y, m, d] = key.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dt = new Date(y, m - 1, d);
  return Math.round((dt.getTime() - today.getTime()) / 86400000);
}

export function fmtDay(key: string | null | undefined, withYear = false): string {
  if (!key) return "—";
  const [y, m, d] = key.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return key;
  return new Date(y, m - 1, d).toLocaleDateString("en-AU", withYear ? { day: "numeric", month: "short", year: "numeric" } : { day: "numeric", month: "short" });
}

/** The one-line standing a card shows: "Overdue · was due 14 Oct", "Due in 9 days · 31 Oct", "Current · next 22 Jan". */
export function standingLine(r: RegisterRow): string {
  if (r.urgency === "out_of_service") {
    const why = r.last_result ? ` · ${r.last_result.toLowerCase() === "fail" ? "failed" : r.last_result.toLowerCase()}${r.last_inspection_date ? " " + fmtDay(r.last_inspection_date) : ""}` : "";
    return `Out of service${why}`;
  }
  if (r.urgency === "never_inspected") return "Never inspected";
  const n = daysUntil(r.next_due);
  if (r.urgency === "overdue") return `Overdue · was due ${fmtDay(r.next_due)}`;
  if (r.urgency === "due_soon") return n === 0 ? `Due today` : `Due in ${n} day${n === 1 ? "" : "s"} · ${fmtDay(r.next_due)}`;
  return `Current · next ${fmtDay(r.next_due)}`;
}

/** Where it lives, in words: "Rigging Kit 4 · V01" / "Depot · Rescue Kit 1" / "Pool (spare)" / "Kept in V03". */
export function homeLine(r: RegisterRow): string {
  if (r.kind === "ladder") return r.kept_in_vehicle_code ? `Kept in ${r.kept_in_vehicle_code}` : "Depot";
  if (!r.kit_name) return "Pool (spare)";
  return r.kit_vehicle_code ? `${r.kit_name} · ${r.kit_vehicle_code}` : `${r.kit_name} · depot`;
}

export function itemHref(r: Pick<RegisterRow, "kind" | "item_id">): string {
  return `/inspections/equipment/${r.kind}/${r.item_id}`;
}
export function inspectHref(r: Pick<RegisterRow, "kind" | "item_id">): string {
  return `/inspections/new?item=${r.item_id}&kind=${r.kind}`;
}

// ---- the tag chip -----------------------------------------------------------

/** The coloured square the riggers recognise: colour + tag number, or "—" / "OUT". */
export function TagChip({ colour, number, out, size = "md" }: { colour: string | null; number: string | null; out?: boolean; size?: "sm" | "md" }) {
  const dim = size === "sm" ? "w-9 h-9 text-[9px]" : "w-12 h-12 text-[10px]";
  if (out) {
    return <div className={`${dim} rounded-lg flex items-center justify-center font-bold text-danger border border-dashed border-danger/50 bg-danger/10 shrink-0`}>OUT</div>;
  }
  const bg = colour ? TAG_SWATCH[colour] : null;
  if (!bg) {
    return <div className={`${dim} rounded-lg flex items-center justify-center text-text-dim border border-dashed border-line bg-ink-raised shrink-0`}>—</div>;
  }
  return (
    <div className={`${dim} rounded-lg flex flex-col items-center justify-center font-bold leading-tight shrink-0`} style={{ background: bg, color: "#111" }}>
      <span className="uppercase">{colour}</span>
      {number && <span className="font-mono font-semibold">{number}</span>}
    </div>
  );
}

// ---- data ----------------------------------------------------------------------

export function useEquipmentRegister(enabled = true) {
  return useQuery({
    queryKey: ["equipment_register"],
    enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_equipment_register")
        .select("*")
        .order("kind")
        .order("code");
      if (error) throw error;
      return (data ?? []) as RegisterRow[];
    },
  });
}

export function useEquipmentKits(enabled = true) {
  return useQuery({
    queryKey: ["equipment_kits"],
    enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("equipment_kits")
        .select("kit_id, kit_name, vehicle_id, is_active, notes, vehicle:vehicle_id(vehicle_code, make, model, rego, assigned_to_employee_id)")
        .order("kit_name");
      if (error) throw error;
      return (data ?? []) as Kit[];
    },
  });
}

/** Open requests — RLS shows a crew member their own, an allocator all of them. */
export function useOpenRequests(enabled = true) {
  return useQuery({
    queryKey: ["equipment_requests_open"],
    enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("equipment_requests")
        .select("request_id, kind, item_id, requested_by_employee_id, requested_for_employee_id, note, status, created_at, requested_by:requested_by_employee_id(first_name, last_name)")
        .eq("status", "open")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as EquipmentRequest[];
    },
  });
}

export function useTntTesters(enabled = true) {
  return useQuery({
    queryKey: ["tnt_testers"],
    enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any).schema("app").rpc("tnt_testers");
      if (error) throw error;
      return (data ?? []) as { employee_id: string; employee_name: string; licence_number: string | null; expiry_date: string | null }[];
    },
  });
}

export function useBackdateDays() {
  return useQuery({
    queryKey: ["inspection_backdate_days"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).schema("app").rpc("inspection_backdate_days");
      if (error) return 14;
      return Number(data ?? 14);
    },
  });
}

function invalidateEquipment(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["equipment_register"] });
  qc.invalidateQueries({ queryKey: ["equipment_requests_open"] });
  qc.invalidateQueries({ queryKey: ["equipment_kits"] });
  qc.invalidateQueries({ queryKey: ["inspection_item"] });
  qc.invalidateQueries({ queryKey: ["inspection_register"] });
}

/** The one-tap actions. Each resolves on success; errors come back as Error with the database's plain message. */
export function useEquipmentActions() {
  const qc = useQueryClient();
  const done = () => invalidateEquipment(qc);
  const rpc = async (fn: string, args: Record<string, unknown>) => {
    const { data, error } = await (supabase as any).schema("app").rpc(fn, args);
    if (error) throw new Error(error.message);
    return data;
  };
  const request  = useMutation({ mutationFn: (v: { kind: Kind; item_id: string; note?: string; for_employee_id?: string | null }) =>
    rpc("equipment_request", { p_kind: v.kind, p_item_id: v.item_id, p_note: v.note ?? null, p_for_employee_id: v.for_employee_id ?? null }), onSuccess: done });
  const allocate = useMutation({ mutationFn: (v: { kind: Kind; item_id: string; holder_employee_id: string | null; kit_id: string | null; reason?: string }) =>
    rpc("equipment_allocate", { p_kind: v.kind, p_item_id: v.item_id, p_holder_employee_id: v.holder_employee_id, p_kit_id: v.kit_id, p_reason: v.reason ?? null }), onSuccess: done });
  const approve  = useMutation({ mutationFn: (v: { kind: Kind; item_id: string }) =>
    rpc("equipment_approve", { p_kind: v.kind, p_item_id: v.item_id }), onSuccess: done });
  const decline  = useMutation({ mutationFn: (v: { request_id: string; reason?: string }) =>
    rpc("equipment_request_decline", { p_request_id: v.request_id, p_reason: v.reason ?? null }), onSuccess: done });
  const resolveDefect = useMutation({ mutationFn: (v: { kind: "rigging" | "power_tool"; inspection_id: string; note: string }) =>
    rpc("resolve_equipment_defect", { p_kind: v.kind, p_inspection_id: v.inspection_id, p_note: v.note }), onSuccess: done });
  return { request, allocate, approve, decline, resolveDefect };
}

// ---- export ------------------------------------------------------------------------

/** The road-week export reads v_inspection_register's names; map ours onto it. */
export function toExportRow(r: RegisterRow): RegisterExportRow {
  return {
    kind: r.kind,
    item_id: r.item_id,
    code: r.code,
    asset_tag: r.asset_tag ?? r.code,
    display_name: r.display_name,
    item_class: r.item_class,
    item_type: r.item_type,
    make: r.make,
    model: r.model,
    serial_number: r.serial_number,
    swl_kg: r.swl_kg,
    kit_name: r.kind === "ladder" ? (r.kept_in_vehicle_code ? `Kept in ${r.kept_in_vehicle_code}` : null) : (r.kit_name ? (r.kit_vehicle_code ? `${r.kit_name} (${r.kit_vehicle_code})` : r.kit_name) : null),
    assigned_to_name: r.holder_name,
    status: r.status,
    tag_colour: r.tag_colour,
    tag_number: r.tag_number,
    last_inspection_date: r.last_inspection_date,
    next_inspection_date: r.next_due,
    last_result: r.last_result,
    last_inspector_name: r.last_inspector_name,
    urgency: r.urgency,
  };
}

/** The road-week export dialog — Excel or PDF, "Prepared for", site, optional history. */
export function ExportDialog({ rows, domainLabel, defaultSite, scopeNote, preparedByName, onClose }: {
  rows: RegisterRow[];
  domainLabel: string;
  defaultSite: string;
  scopeNote: string;
  preparedByName: string;
  onClose: () => void;
}) {
  const [preparedFor, setPreparedFor]       = useState("");
  const [siteLabel, setSiteLabel]           = useState(defaultSite);
  const [includeHistory, setIncludeHistory] = useState(true);
  const [busy, setBusy]                     = useState<"xlsx" | "pdf" | null>(null);
  const [err, setErr]                       = useState<string | null>(null);

  const run = async (format: "xlsx" | "pdf") => {
    setBusy(format); setErr(null);
    try {
      // lazy — the exporters pull exceljs / pdfmake, which stay out of the main bundle
      const mod = await import("@/features/inspections/inspectionRegisterExport");
      const opts = { rows: rows.map(toExportRow), domainLabel, preparedFor, siteLabel, scopeNote, includeHistory, preparedByName };
      if (format === "xlsx") await mod.exportRegisterXlsx(opts);
      else await mod.exportRegisterPdf(opts);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[15px] font-semibold flex items-center gap-2"><Download size={15} className="text-accent"/> Export register</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-2.5 -m-2.5 rounded-md"><X size={16}/></button>
        </div>
        <p className="text-[12px] text-text-muted mb-3">
          {rows.length} item{rows.length === 1 ? "" : "s"} · {domainLabel}
          {scopeNote && <span className="block text-[11px] text-text-dim mt-0.5">{scopeNote}</span>}
        </p>
        <Field label="Prepared for" hint="Client or person asking, as it should print. Leave blank for an internal copy.">
          <Input value={preparedFor} onChange={e => setPreparedFor(e.target.value)} placeholder="e.g. the client's field manager"/>
        </Field>
        <Field label="Site / job">
          <Input value={siteLabel} onChange={e => setSiteLabel(e.target.value)} placeholder="e.g. Mount Tamborine NBN"/>
        </Field>
        <label className="flex items-center gap-2 text-[12px] text-text mb-4 cursor-pointer select-none">
          <input type="checkbox" checked={includeHistory} onChange={e => setIncludeHistory(e.target.checked)} className="accent-accent"/>
          Include every inspection on record for these items (second sheet / second section)
        </label>
        {err && <div className="mb-3 px-3 py-2 rounded bg-danger/10 border border-danger/30 text-danger text-[12px]">{err}</div>}
        <div className="flex gap-2">
          <Button variant="secondary" icon={FileSpreadsheet} onClick={() => run("xlsx")} disabled={!!busy} className="flex-1 justify-center">
            {busy === "xlsx" ? "Building…" : "Excel"}
          </Button>
          <Button variant="primary" icon={FileText} onClick={() => run("pdf")} disabled={!!busy} className="flex-1 justify-center">
            {busy === "pdf" ? "Building…" : "PDF"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- allocate modal (one tap: holder + kit) ------------------------------------------

export function AllocateDialog({ row, kits, employees, onClose, onDone }: {
  row: RegisterRow;
  kits: Kit[];
  employees: { employee_id: string; first_name: string | null; last_name: string | null }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { allocate } = useEquipmentActions();
  const [holder, setHolder] = useState<string>(row.holder_employee_id ?? "");
  const [kit, setKit]       = useState<string>(row.kit_id ?? "");
  const [reason, setReason] = useState("");
  const [err, setErr]       = useState<string | null>(null);
  const name = (e: { first_name: string | null; last_name: string | null }) => [e.first_name, e.last_name].filter(Boolean).join(" ");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-t-2xl md:rounded-lg w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[15px] font-semibold">Allocate</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-2.5 -m-2.5 rounded-md"><X size={16}/></button>
        </div>
        <p className="text-[12px] text-text-muted mb-3">
          <span className="text-text">{row.code} · {row.display_name}</span> — who holds it, and which kit it lives in. Both are optional: no holder + no kit = the pool.
        </p>
        <Field label="Holder (person)">
          <select value={holder} onChange={e => setHolder(e.target.value)} className="w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text">
            <option value="">— nobody —</option>
            {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{name(e)}</option>)}
          </select>
        </Field>
        <Field label={row.kind === "ladder" ? "Kit (sets the vehicle it is kept in)" : "Kit (home)"}>
          <select value={kit} onChange={e => setKit(e.target.value)} className="w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text">
            <option value="">— pool / depot —</option>
            {kits.filter(k => k.is_active).map(k => (
              <option key={k.kit_id} value={k.kit_id}>{k.kit_name}{k.vehicle ? ` · ${k.vehicle.vehicle_code}` : " · depot"}</option>
            ))}
          </select>
        </Field>
        <Field label="Note (optional)">
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Walcha job"/>
        </Field>
        {err && <div className="mb-3 px-3 py-2 rounded bg-danger/10 border border-danger/30 text-danger text-[12px]">{err}</div>}
        <div className="flex gap-2 mt-2">
          <Button variant="secondary" onClick={onClose} className="flex-1 justify-center">Cancel</Button>
          <Button variant="primary" disabled={allocate.isPending} className="flex-1 justify-center"
            onClick={() => allocate.mutate(
              { kind: row.kind, item_id: row.item_id, holder_employee_id: holder || null, kit_id: kit || null, reason: reason.trim() || undefined },
              { onSuccess: onDone, onError: (e: any) => setErr(e?.message ?? "Could not allocate") },
            )}>
            {allocate.isPending ? "Saving…" : "Allocate"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** A bottom-sheet prompt for a one-line reason — request, decline, resolve. */
export function ReasonSheet({ title, blurb, placeholder, cta, required, busy, onClose, onSubmit, tone = "primary" }: {
  title: string; blurb?: string; placeholder: string; cta: string; required?: boolean; busy?: boolean;
  onClose: () => void; onSubmit: (text: string) => void; tone?: "primary" | "danger";
}) {
  const [text, setText] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-t-2xl md:rounded-lg w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="text-[15px] font-semibold mb-1">{title}</div>
        {blurb && <p className="text-[12px] text-text-muted mb-3">{blurb}</p>}
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder={placeholder}
          className="w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text mb-3"/>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose} className="flex-1 justify-center">Cancel</Button>
          <Button variant={tone} disabled={!!busy || (required && !text.trim())} className="flex-1 justify-center" onClick={() => onSubmit(text.trim())}>
            {busy ? "Saving…" : cta}
          </Button>
        </div>
      </div>
    </div>
  );
}
